import React, { useState, useEffect } from "react";
import { SolutionProject } from "../types";
import { fetchUserSolutionProjects } from "../api";
import { formatDueDateWithTimezone, isOverdue } from "../utils/dateFormatting";
import "./SolutionProjectList.css";

type TabType = "projects";

interface SolutionProjectListProps {
  onSelectProject: (projectId: string) => void;
  activeTab?: TabType;
  onTabChange?: (tab: TabType) => void;
  userPermissions?: string[];
  loading?: boolean;
  userId?: string | null;
}

const SolutionProjectList: React.FC<SolutionProjectListProps> = ({
  onSelectProject,
  activeTab = "projects",
  onTabChange,
  userPermissions = [],
  loading: externalLoading = false,
  userId,
}) => {
  const [projects, setProjects] = useState<SolutionProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const userProjects = await fetchUserSolutionProjects();
      setProjects(userProjects);
      setError(null);
    } catch (err: any) {
      console.error("Error loading solution projects:", err);
      setError("Failed to load solution projects. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    if (loading) {
      return <div className="loading">Loading projects...</div>;
    }

    if (error) {
      return (
        <>
          <div className="error-message">{error}</div>
          <button onClick={loadProjects} className="retry-button">
            Retry
          </button>
        </>
      );
    }

    if (projects.length === 0) {
      return (
        <div className="no-projects">
          No solution projects assigned to you yet.
        </div>
      );
    }

    return (
      <div className="projects-content">
        <h2>Your Solution Projects</h2>
        <div className="project-grid">
          {projects.map((project) => {
            const isCompleted = project.progress?.completed === project.progress?.total;
            const isPastDue = isOverdue(project.due_date);
            const isLocked = isPastDue && !isCompleted;

            return (
              <div
                key={project.id}
                className={`project-card ${isCompleted ? "completed" : ""} ${isLocked ? "locked" : ""}`}
                onClick={() => !isLocked && onSelectProject(project.id)}
              >
                <div className="project-header">
                  <h3>{project.name}</h3>
                  {isCompleted && (
                    <div className="completion-badge">✓ Complete</div>
                  )}
                  {isLocked && (
                    <div className="locked-badge">🔒 Locked</div>
                  )}
                </div>
                <div className="project-due-date">
                  <span className="due-label">Due:</span>
                  <span className={`due-value ${isCompleted ? "completed" : isPastDue ? "overdue" : ""}`}>
                    {formatDueDateWithTimezone(project.due_date)}
                  </span>
                </div>
                <div className="project-details">
                  <div className="detail-item">
                    <span className="label">Software:</span>
                    <span className="value">{project.software.toUpperCase()}</span>
                  </div>
                </div>
                {project.progress && (
                  <div className="project-progress">
                    <span className="progress-text">
                      {project.progress.completed} / {project.progress.total} tasks
                    </span>
                  </div>
                )}
                {project.total_earned !== undefined && (
                  <div className="project-earnings">
                    <span className="earnings-amount">${project.total_earned.toFixed(2)}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="project-list-container">
      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === "projects" ? "active" : ""}`}
            onClick={() => onTabChange?.("projects")}
          >
            Projects
          </button>
        </div>
      </div>
      
      {activeTab === "projects" && renderTabContent()}
    </div>
  );
};

export default SolutionProjectList;